// src/pages/Login.jsx
import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import ErrorMessage from '../components/ErrorMessage'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
export default function Login() {
  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const [showPass, setShowPass] = useState(false)

  const { login } = useAuth()
  const navigate  = useNavigate()

  // Set by AuthContext's idle-timeout logout so this doesn't look like an
  // unexplained silent redirect back to Login.
  const [idleNotice, setIdleNotice] = useState(false)
  useEffect(() => {
    try {
      if (sessionStorage.getItem('kailux_idle_logout') === '1') {
        setIdleNotice(true)
        sessionStorage.removeItem('kailux_idle_logout')
      }
    } catch { /* ignore */ }
  }, [])

  const handleLogin = async (e) => {
    e?.preventDefault()
    if (!email || !password) { setError('Please fill in all fields'); return }
    setLoading(true); setError('')

    const { data, error: authError } = await login(email, password)

    if (authError) {
      setError('Incorrect email or password. Please try again.')
      setLoading(false)
    } else {
      const role = data?.user?.app_metadata?.role
      const isAdminHost = window.location.hostname.startsWith('admin.')

      if (isAdminHost) {
        // This surface is admin-only — a firm user has no business here.
        if (role === 'admin') {
          navigate('/admin')
        } else {
          setError('This login is for KaiLux admin staff only. Firm users should log in at app.kailux.co.za.')
          setLoading(false)
        }
      } else if (role === 'admin') {
        // Admin Board has moved to its own subdomain — there's no /admin
        // route here anymore to send them to.
        window.location.href = 'https://admin.kailux.co.za/login'
      } else {
        navigate('/dashboard')
      }
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg,
      fontFamily:"'Outfit',sans-serif", color:T.tx,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'24px' }}>

      {/* Background glow */}
      <div style={{ position:'fixed', top:'20%', left:'50%', transform:'translateX(-50%)',
        width:'600px', height:'300px', pointerEvents:'none',
        background:`radial-gradient(ellipse,${alpha(T.or, 0.08)} 0%,transparent 70%)` }}/>

      <div style={{ width:'100%', maxWidth:'420px', position:'relative', zIndex:1 }}>

        {/* Logo */}
        <div style={{ textAlign:'center', marginBottom:'36px' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:'8px' }}>
            <div style={{ width:'40px', height:'40px', borderRadius:'12px',
              background:T.prg,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontFamily:"'Syne',sans-serif", fontWeight:'800',
              color:T.onpri, fontSize:'18px' }}>K</div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'24px', fontWeight:'800' }}>
              Kai<span style={{ color:T.or }}>Lux</span>
            </div>
          </div>
          <div style={{ fontSize:'14px', color:T.mt, marginTop:'6px' }}>
            AI Financial Statement Engine
          </div>
        </div>

        {/* Card */}
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
          borderRadius:'20px', padding:'32px',
          boxShadow:T.shl }}>

          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
            fontWeight:'800', marginBottom:'6px' }}>Welcome back</div>
          <div style={{ fontSize:'14px', color:T.mt, marginBottom:'28px' }}>
            Sign in to your KaiLux firm account
          </div>

          {idleNotice && !error && (
            <div style={{
              marginBottom:'16px', padding:'10px 14px', borderRadius:'8px',
              background:alpha(T.or, 0.08), border:`1px solid ${alpha(T.or, 0.3)}`,
              color:T.lt, fontSize:'13px',
            }}>
              You were signed out after a period of inactivity. Your work up to that point was already saved.
            </div>
          )}
          {error && <ErrorMessage style={{ marginBottom:'16px' }}>{error}</ErrorMessage>}

          <form onSubmit={handleLogin}>
            {/* Email */}
            <div style={{ marginBottom:'14px' }}>
              <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                EMAIL ADDRESS
              </label>
              <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                placeholder="you@yourfirm.co.za" autoComplete="email"
                style={{ width:'100%', padding:'12px 14px', background:T.s2,
                  border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                  fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                  outline:'none', boxSizing:'border-box' }}
                onFocus={e=>e.target.style.borderColor=T.or}
                onBlur={e=>e.target.style.borderColor=T.bd}/>
            </div>

            {/* Password */}
            <div style={{ marginBottom:'8px' }}>
              <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>
                PASSWORD
              </label>
              <input type={showPass?'text':'password'} value={password}
                onChange={e=>setPassword(e.target.value)}
                placeholder="Your password" autoComplete="current-password"
                style={{ width:'100%', padding:'12px 14px', background:T.s2,
                  border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                  fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                  outline:'none', boxSizing:'border-box' }}
                onFocus={e=>e.target.style.borderColor=T.or}
                onBlur={e=>e.target.style.borderColor=T.bd}/>
            </div>

            <div style={{ display:'flex', justifyContent:'space-between',
              alignItems:'center', marginBottom:'20px' }}>
              <label style={{ display:'flex', gap:'4px', alignItems:'center',
                fontSize:'13px', color:T.lt, cursor:'pointer' }}>
                <input type="checkbox" checked={showPass}
                  onChange={e=>setShowPass(e.target.checked)}
                  style={{ accentColor:T.or }}/>
                Show password
              </label>
              <Link to="/forgot-password" style={{ fontSize:'13px', color:T.or, textDecoration:'none' }}>
                Forgot password?
              </Link>
            </div>

            <button type="submit" disabled={loading}
              style={{ width:'100%', padding:'12px', borderRadius:'8px', border:'none',
                background:loading?T.mt:T.prg,
                color:T.onpri, fontFamily:"'Outfit',sans-serif",
                fontSize:'15px', fontWeight:'700', cursor:loading?'not-allowed':'pointer',
                boxShadow:loading?'none':`0 4px 20px ${alpha(T.pri, 0.35)}`,
                display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
              {loading ? (
                <>
                  <span style={{ width:'14px', height:'14px',
                    border:`2px solid ${alpha(T.onpri, 0.3)}`,
                    borderTopColor:T.onpri, borderRadius:'50%',
                    animation:'spin 0.8s linear infinite', display:'inline-block' }}/>
                  Signing in...
                </>
              ) : 'Sign In →'}
            </button>
          </form>

          <div style={{ textAlign:'center', marginTop:'20px',
            fontSize:'14px', color:T.mt }}>
            Don't have an account?{' '}
            <Link to="/signup" style={{ color:T.or, fontWeight:'700',
              textDecoration:'none' }}>Create an account</Link>
          </div>
        </div>

        {/* Trust */}
        <div style={{ display:'flex', justifyContent:'center', gap:'20px',
          marginTop:'24px', flexWrap:'wrap' }}>
          {['IFRS for SMEs','POPIA Compliant',<><Icon name="pin" /> South African Built</>].map((t,i)=>(
            <span key={i} style={{ fontSize:'12px', color:T.mt,
              display:'flex', alignItems:'center', gap:'4px' }}>
              <span style={{ color:T.gn }}><Icon name="check" /></span>{t}
            </span>
          ))}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
